import type { RecordLikeProtocol } from '@lighthouse/core'
import { isSameDay, startOfDay } from 'date-fns'

export interface SpannerSegment {
    record: RecordLikeProtocol
    startCol: number
    colSpan: number
    /** 0-based row index within spanning rows for this week (used for gridRow: index + 2) */
    index: number
}

/**
 * Maps a Date to a 1-based column index for the given week.
 * Derived from week[0].getDay() so it works for any WEEK_STARTS_ON value.
 * e.g. weekStartsOn=1 (Mon): Mon=1…Sun=7; weekStartsOn=0 (Sun): Sun=1, Mon=2…Sat=7
 */
const getDayColumn = (date: Date, weekStartDay: number): number => {
    const day = date.getDay() // 0=Sun, 1=Mon, …, 6=Sat
    return ((day - weekStartDay + 7) % 7) + 1
}

/**
 * Computes spanning record segments for a single week row.
 * Single-day records (startDate == endDate) are excluded.
 * Multi-day records are clipped to the week's boundaries.
 */
export function computeWeekSpanners(
    records: RecordLikeProtocol[],
    startField: string,
    endField: string,
    week: Date[]
): SpannerSegment[] {
    const weekStart = week[0]
    const weekEnd = week[6]
    // Derive week-start day from the actual first element so the mapping is
    // always correct regardless of WEEK_STARTS_ON configuration.
    const weekStartDay = weekStart.getDay()

    const segments: SpannerSegment[] = []

    for (const record of records) {
        const startVal = record.content[startField]?.value
        const endVal = record.content[endField]?.value
        if (typeof startVal !== 'number' || typeof endVal !== 'number' || startVal > endVal) {
            continue
        }
        if (isSameDay(startVal, endVal)) {
            continue
        }

        const recStart = startOfDay(new Date(startVal))
        const recEnd = startOfDay(new Date(endVal))

        // Skip records that don't overlap with this week
        if (recStart > weekEnd || recEnd < weekStart) {
            continue
        }

        // Clip to week boundaries
        const segStart = recStart < weekStart ? weekStart : recStart
        const segEnd = recEnd > weekEnd ? weekEnd : recEnd

        const startCol = getDayColumn(segStart, weekStartDay)
        const endCol = getDayColumn(segEnd, weekStartDay)
        const colSpan = endCol - startCol + 1

        if (colSpan <= 0) {
            continue
        }

        segments.push({ record, startCol, colSpan, index: 0 })
    }

    if (segments.length === 0) {
        return segments
    }

    // Sort by start column, then by width (wider spans first) for stable layout
    segments.sort((a, b) => a.startCol - b.startCol || b.colSpan - a.colSpan)

    // Assign non-overlapping row indices using a 7-column boolean grid
    const virtualZone: boolean[][] = [Array.from<boolean>({ length: 7 }).fill(false)]
    for (const seg of segments) {
        for (let rowIdx = 0; ; rowIdx++) {
            if (rowIdx >= virtualZone.length) {
                virtualZone.push(Array.from<boolean>({ length: 7 }).fill(false))
            }
            const sliceStart = seg.startCol - 1
            const occupied = virtualZone[rowIdx].slice(sliceStart, sliceStart + seg.colSpan).some(Boolean)
            if (!occupied) {
                for (let c = sliceStart; c < sliceStart + seg.colSpan; c++) {
                    virtualZone[rowIdx][c] = true
                }
                seg.index = rowIdx
                break
            }
        }
    }

    return segments
}
